import type {
  CmmdProject,
  CmmdTaskExecutionOption,
  EnvironmentApi,
  EnvironmentId,
  ProjectId,
} from "@cmmd-forge/contracts";

import { mapCmmdStatusToRuntimeStatus } from "~/cmmd/forgeRuntimeStatus";
import { readEnvironmentApi } from "~/environmentApi";
import { ensureDesktopRuntimeEnvironmentApi } from "~/lib/ensureDesktopRuntimeEnvironmentApi";
import type { CloudEnvironmentProjectSlice } from "../sidebar/cloudRuntimeRowState.logic";
import { connectCloudProjectRuntime } from "../sidebar/connectCloudRuntime";
import type { SidebarPreConnectCloudMetadata } from "../sidebar/SidebarProjectItem";
import type { TaskWorkSessionTarget } from "./taskWorkSessionLaunch";

export interface TaskWorkConnectionTarget extends TaskWorkSessionTarget {
  readonly organizationId: number;
  readonly runtime: CmmdTaskExecutionOption["runtime"];
  readonly workspaceRoot: string | null;
  readonly githubRepoFullName: string | null;
}

interface TaskWorkTargetConnectionDependencies {
  readonly readApi: (environmentId: EnvironmentId) => EnvironmentApi | undefined;
  /** Resolves to the runtime id CMMD settled on (provisioning if needed). */
  readonly connectCloudRuntime: (metadata: SidebarPreConnectCloudMetadata) => Promise<string>;
  readonly ensureDesktopRuntimeApi: typeof ensureDesktopRuntimeEnvironmentApi;
}

const DEFAULT_DEPENDENCIES: TaskWorkTargetConnectionDependencies = {
  readApi: readEnvironmentApi,
  connectCloudRuntime: connectCloudProjectRuntime,
  ensureDesktopRuntimeApi: ensureDesktopRuntimeEnvironmentApi,
};

export async function ensureTaskWorkTargetConnection(input: {
  readonly target: TaskWorkConnectionTarget;
  readonly project: CmmdProject;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly dependencies?: TaskWorkTargetConnectionDependencies;
}): Promise<void> {
  const dependencies = input.dependencies ?? DEFAULT_DEPENDENCIES;
  const environmentId = input.target.environmentId as EnvironmentId;
  if (dependencies.readApi(environmentId)) return;

  if (input.target.runtime.source === "forge-cloud") {
    await dependencies.connectCloudRuntime(buildCloudRuntimeMetadata(input.project, input.target));
    if (!dependencies.readApi(environmentId)) {
      throw new Error("Cloud project connected without exposing its Forge command API.");
    }
    return;
  }

  const api = await dependencies.ensureDesktopRuntimeApi({
    environmentId,
    primaryEnvironmentId: input.primaryEnvironmentId,
    label: input.target.runtime.displayName,
    readApi: dependencies.readApi,
  });
  if (!api && environmentId !== input.primaryEnvironmentId) {
    throw new Error("The selected Forge device is not connected.");
  }
}

export function resolveTaskWorkTargetProjectRef(input: {
  readonly target: TaskWorkConnectionTarget;
  readonly cmmdProjectId: string;
  readonly environmentStateById: Readonly<Record<string, CloudEnvironmentProjectSlice | undefined>>;
}): { readonly environmentId: EnvironmentId; readonly projectId: ProjectId } {
  if (input.target.runtime.source !== "forge-cloud") {
    return {
      environmentId: input.target.environmentId as EnvironmentId,
      projectId: input.target.projectId as ProjectId,
    };
  }

  const environmentId = input.target.environmentId as EnvironmentId;
  const environment = input.environmentStateById[environmentId];
  if (!environment) {
    throw new Error("The connected cloud project did not expose the linked Forge project.");
  }

  const canonicalProjectId = resolveCanonicalProjectId(environment, [
    input.target.projectId as ProjectId,
    input.cmmdProjectId as ProjectId,
  ]);
  if (canonicalProjectId) {
    return {
      environmentId,
      projectId: canonicalProjectId,
    };
  }

  return {
    environmentId,
    projectId: resolveMetadataProjectId(environment, input.target),
  };
}

function resolveCanonicalProjectId(
  environment: CloudEnvironmentProjectSlice,
  candidateIds: readonly ProjectId[],
): ProjectId | null {
  for (const candidateId of candidateIds) {
    if (!environment.projectIds.includes(candidateId)) continue;
    if (environment.projectById[candidateId]?.id === candidateId) return candidateId;
  }
  return null;
}

function resolveMetadataProjectId(
  environment: CloudEnvironmentProjectSlice,
  target: TaskWorkConnectionTarget,
): ProjectId {
  const projects = environment.projectIds.flatMap((projectId) => {
    const project = environment.projectById[projectId];
    return project ? [project] : [];
  });
  const pathMatches = findWorkspacePathMatches(projects, target.workspaceRoot);
  const repositoryMatches = findRepositoryMatches(projects, target.githubRepoFullName);

  if (pathMatches.length === 1) {
    const [pathMatch] = pathMatches;
    if (!pathMatch) {
      throw new Error("The linked project path could not be resolved.");
    }
    if (repositoryMatches.length === 0 || repositoryMatches.includes(pathMatch)) {
      return pathMatch.id as ProjectId;
    }
    throw new Error("The linked project path and repository identify different Forge projects.");
  }
  if (pathMatches.length > 1) {
    throw new Error("The linked project path matched more than one Forge project.");
  }
  if (repositoryMatches.length === 1) {
    return requireSingleProjectId(
      repositoryMatches[0],
      "The linked repository could not be resolved.",
    );
  }
  if (repositoryMatches.length > 1) {
    throw new Error("The linked repository matched more than one Forge project.");
  }
  throw new Error("The connected cloud project did not expose the linked Forge project.");
}

function findWorkspacePathMatches(
  projects: readonly CloudEnvironmentProjectSlice["projectById"][string][],
  workspaceRoot: string | null,
) {
  const normalizedWorkspaceRoot = normalizeWorkspacePath(workspaceRoot);
  return normalizedWorkspaceRoot
    ? projects.filter((project) => normalizeWorkspacePath(project.cwd) === normalizedWorkspaceRoot)
    : [];
}

function findRepositoryMatches(
  projects: readonly CloudEnvironmentProjectSlice["projectById"][string][],
  repositoryFullName: string | null,
) {
  const normalizedRepositoryName = normalizeRepositoryFullName(repositoryFullName);
  return normalizedRepositoryName
    ? projects.filter((project) =>
        repositoryIdentityNames(project).includes(normalizedRepositoryName),
      )
    : [];
}

function repositoryIdentityNames(
  project: CloudEnvironmentProjectSlice["projectById"][string],
): readonly string[] {
  const ownerAndName =
    project.repositoryIdentity?.owner && project.repositoryIdentity.name
      ? `${project.repositoryIdentity.owner}/${project.repositoryIdentity.name}`
      : null;
  return [
    project.repositoryIdentity?.canonicalKey,
    ownerAndName,
    project.repositoryIdentity?.locator.remoteUrl,
  ].flatMap((value) => {
    const normalized = normalizeRepositoryFullName(value);
    return normalized ? [normalized] : [];
  });
}

function requireSingleProjectId(
  project: CloudEnvironmentProjectSlice["projectById"][string] | undefined,
  errorMessage: string,
): ProjectId {
  if (!project) throw new Error(errorMessage);
  return project.id as ProjectId;
}

function normalizeWorkspacePath(value: string | null | undefined): string | null {
  const normalized = value?.trim().replaceAll("\\", "/").replace(/\/+/g, "/");
  if (!normalized) return null;
  return normalized.length > 1 ? normalized.replace(/\/+$/, "") : normalized;
}

function normalizeRepositoryFullName(value: string | null | undefined): string | null {
  const normalized = value
    ?.trim()
    .replace(/^git@[^:]+:/i, "")
    .replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+\//i, "")
    .replace(/^github\.com\//i, "")
    .replace(/\.git$/i, "")
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
  if (!normalized) return null;
  const segments = normalized.split("/").filter(Boolean);
  return segments.length >= 2 ? segments.slice(-2).join("/") : null;
}

function buildCloudRuntimeMetadata(
  project: CmmdProject,
  target: TaskWorkConnectionTarget,
): SidebarPreConnectCloudMetadata {
  return {
    runtimeId: target.runtime.id,
    cmmdOrganizationId: requirePositiveId(target.organizationId, "organization"),
    cmmdProjectId: requirePositiveId(project.id, "project"),
    // Task execution targets do not carry the catalog registration generation.
    // Fail closed: this path may connect, but cannot mint a persisted handoff.
    registeredAt: null,
    projectName: project.title,
    githubRepoFullName: target.githubRepoFullName,
    workspaceRoot: target.workspaceRoot,
    iconUrl: project.iconUrl ?? null,
    icon: project.icon ?? null,
    status: mapCmmdStatusToRuntimeStatus(
      target.runtime.status,
      readMetadataString(target.runtime.metadata, "readinessPhase"),
    ),
  };
}

function requirePositiveId(value: number | string | null | undefined, label: string): number {
  const parsed =
    typeof value === "number" || typeof value === "string" ? Number(value) : Number.NaN;
  if (!Number.isSafeInteger(parsed) || parsed <= 0) {
    throw new Error(`CMMD did not provide the ${label} identity required for this connection.`);
  }
  return parsed;
}

function readMetadataString(
  metadata: Readonly<Record<string, unknown>> | null,
  key: string,
): string | undefined {
  const value = metadata?.[key];
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}
